import { useBlur } from '../../app/BlurContext.js';
import { cn } from '../../lib/cn.js';
import { useMediaSource, type LoadingPhase } from './useMediaSource.js';

interface Props {
  id: string;
  url: string | null;
  albumKey: CryptoKey;
  alt: string;
  className?: string;
  mimeType?: string;
  /** Some callers (the grid) gate decryption on viewport entry — they pass
   *  false until the tile is visible to avoid a thundering herd of decrypts
   *  on first album open. */
  enabled?: boolean;
}

const PHASE_LABEL: Record<LoadingPhase, string> = {
  'awaiting-url': 'Richiesta link…',
  'cache-thumb': 'Cache anteprima…',
  'cache-cipher': 'Cache locale…',
  fetching: 'Scarico…',
  decrypting: 'Decifro…',
  thumbnailing: 'Anteprima…',
};

// Thin renderer over useMediaSource. Asks for the thumbnail path so the
// grid stays cheap on reload; the hook transparently falls back to a full
// decrypt on first visit (and seeds the thumb cache for next time).
export function EncryptedImage({
  id,
  url,
  albumKey,
  alt,
  className,
  mimeType,
  enabled = true,
}: Props) {
  const source = useMediaSource(
    enabled ? id : null,
    enabled ? url : null,
    enabled ? albumKey : null,
    { thumb: true, ...(mimeType ? { mimeType } : {}) },
  );
  const { blurred } = useBlur();

  if (source.state === 'error') {
    return (
      <div
        className={`flex items-center justify-center bg-surface-container-low text-xs text-error ${className ?? ''}`}
      >
        {source.error}
      </div>
    );
  }
  // For videos we don't fall back to a full decrypt to render a tile (too
  // expensive). The thumb is generated lazily the first time the user opens
  // the viewer; until then the grid's play-circle overlay sits on top of
  // this neutral placeholder.
  if (source.state === 'no-preview') {
    return (
      <div
        className={`bg-gradient-to-br from-surface-container-low to-surface-container ${className ?? ''}`}
        aria-label={alt}
      />
    );
  }
  if (source.state !== 'ready' || !source.blobUrl) {
    const label = enabled ? (source.phase ? PHASE_LABEL[source.phase] : '') : 'In attesa…';
    return (
      <div
        className={`flex animate-pulse items-center justify-center bg-surface-container-low ${className ?? ''}`}
        aria-label={alt}
      >
        {label ? (
          <span className="px-2 text-center text-[10px] font-medium uppercase tracking-[0.18em] text-on-surface-variant/70 sm:text-xs">
            {label}
          </span>
        ) : null}
      </div>
    );
  }
  return (
    <img
      src={source.blobUrl}
      alt={alt}
      className={cn(className, blurred && 'scale-110 blur-3xl')}
      loading="lazy"
    />
  );
}
